import {yupResolver} from '@hookform/resolvers/yup';
import Fuse from 'fuse.js';
import moment from 'moment';
import React, {useEffect, useRef, useState} from 'react';
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {
  FlatList,
  SafeAreaView,
  Text,
  TextInput as TextInput1,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from '@react-native-vector-icons/fontawesome';
import {useSelector} from 'react-redux';
import * as yup from 'yup';
import {ShowToast, storeSetting, retrieveSetting} from '../Common/common';
import BTButton from '../Components/BTButton';
import BTComboBox from '../Components/BTComboBox';
import {Col, Row} from '../Components/BTGrid';
import {TextInput} from '../Components/BTTextInput';
import {useGetDepartmentsQuery} from '../redux/services/emmdepartmentApi';
import {useGetEmployeesQuery} from '../redux/services/emmemployeeApi';
import {
  useInsertIndentMutation,
  useUpdateIndentMutation,
} from '../redux/services/steindentApi';
import {useGetSTMGroupsQuery} from '../redux/services/stmgroupApi';
import {useGetSTMProductsQuery} from '../redux/services/stmproductApi';
import {RootState} from '../redux/store';
import styles from '../styles/Styles';

import T from '../Components/T';
import { tp } from '../utils/translateText';
interface item {
  pr_group?: number;
  pr_id: number;
  pr_name?: string;
  pr_srate: number;
  pr_qty: number;
  pr_nameed: boolean;
  value: po_mproduct;
}

interface Quantities {
  [key: string]: {
    descr: string;
    quantity: number;
    rate: number;
    remarks: string;
    nameed: boolean;
  };
}

const validationSchema = yup.object().shape({
  it_department: yup
    .number()
    .required()
    .typeError('Select Department?')
    .min(1, 'Select Department'),
});

const POSProduct = ({navigation, route}: UniversalProps) => {
  const inputRef = useRef(null);
  const gdata: any = useSelector((state: RootState) => state.gdata);
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState(0);
  const [cart, setCart] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [quantities, setQuantities] = useState<Quantities>({});
  const [localDepts, setLocalDepts] = useState<any[]>([]);
  const [localEmployees, setLocalEmployees] = useState<any[]>([]);
  const [selectedDept, setSelectedDept] = useState<any>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const cid = gdata.company;

  // Local storage keys
  const DEPT_STORAGE_KEY = `departments_${cid}`;
  const EMPLOYEE_STORAGE_KEY = `employees_${cid}`;
  const SELECTED_DEPT_KEY = `selected_dept_${cid}`;
  const SELECTED_EMPLOYEE_KEY = `selected_employee_${cid}`;

  // Functions to handle local storage
  const storeDepartments = async (departments: any[]) => {
    try {
      await storeSetting(DEPT_STORAGE_KEY, JSON.stringify(departments));
    } catch (error) {
      console.error('Error storing departments:', error);
    }
  };

  const storeEmployees = async (employees: any[]) => {
    try {
      await storeSetting(EMPLOYEE_STORAGE_KEY, JSON.stringify(employees));
    } catch (error) {
      console.error('Error storing employees:', error);
    }
  };

  const loadDepartmentsFromStorage = async () => {
    try {
      const stored = await retrieveSetting(DEPT_STORAGE_KEY);
      if (stored) {
        const departments = JSON.parse(stored);
        setLocalDepts(departments);
        return departments;
      }
    } catch (error) {
      console.error('Error loading departments from storage:', error);
    }
    return [];
  };

  const loadEmployeesFromStorage = async () => {
    try {
      const stored = await retrieveSetting(EMPLOYEE_STORAGE_KEY);
      if (stored) {
        const employees = JSON.parse(stored);
        setLocalEmployees(employees);
        return employees;
      }
    } catch (error) {
      console.error('Error loading employees from storage:', error);
    }
    return [];
  };

  // Functions to handle selected values storage
  const storeSelectedDept = async (dept: any) => {
    try {
      await storeSetting(SELECTED_DEPT_KEY, JSON.stringify(dept));
      setSelectedDept(dept);
    } catch (error) {
      console.error('Error storing selected department:', error);
    }
  };

  const storeSelectedEmployee = async (employee: any) => {
    try {
      await storeSetting(SELECTED_EMPLOYEE_KEY, JSON.stringify(employee));
      setSelectedEmployee(employee);
    } catch (error) {
      console.error('Error storing selected employee:', error);
    }
  };

  const loadSelectedDept = async () => {
    try {
      const stored = await retrieveSetting(SELECTED_DEPT_KEY);
      if (stored) {
        const dept = JSON.parse(stored);
        setSelectedDept(dept);
        return dept;
      }
    } catch (error) {
      console.error('Error loading selected department:', error);
    }
    return null;
  };

  const loadSelectedEmployee = async () => {
    try {
      const stored = await retrieveSetting(SELECTED_EMPLOYEE_KEY);
      if (stored) {
        const employee = JSON.parse(stored);
        setSelectedEmployee(employee);
        return employee;
      }
    } catch (error) {
      console.error('Error loading selected employee:', error);
    }
    return null;
  };

  // Change event handlers
  const handleDeptChange = (selectedValue: any) => {
    try {
      const selectedDeptData = currentDeptData?.find(
        dept => dept.dp_id === selectedValue,
      );
      if (selectedDeptData) {
        storeSelectedDept(selectedDeptData);
        if (methods) {
          methods.setValue('it_department', selectedValue);
        }
      }
    } catch (error) {
      console.error('Error handling department change:', error);
    }
  };

  const handleEmployeeChange = (selectedValue: any) => {
    try {
      const selectedEmployeeData = currentEmployeeData?.find(
        emp => emp.em_id === selectedValue,
      );
      if (selectedEmployeeData) {
        storeSelectedEmployee(selectedEmployeeData);
        if (methods) {
          methods.setValue('it_name', selectedValue);
        }
      }
    } catch (error) {
      console.error('Error handling employee change:', error);
    }
  };

  // Function to clear stored selections
  const clearStoredSelections = async () => {
    try {
      await storeSetting(SELECTED_DEPT_KEY, '');
      await storeSetting(SELECTED_EMPLOYEE_KEY, '');
      setSelectedDept(null);
      setSelectedEmployee(null);
    } catch (error) {
      console.error('Error clearing stored selections:', error);
    }
  };

  // Function to reset form while preserving selected department and employee
  const resetFormWithSelections = () => {
    try {
      if (methods) {
        methods.reset({
          ...initialValues,
          it_department: selectedDept?.dp_id || '',
          it_name: selectedEmployee?.em_id || '',
        });
      }
      setQuantities({});
    } catch (error) {
      console.error('Error resetting form with selections:', error);
    }
  };

  // Function to completely clear everything including stored selections
  const clearEverything = async () => {
    try {
      await clearStoredSelections();
      if (methods) {
        methods.reset(initialValues);
      }
      setQuantities({});
    } catch (error) {
      console.error('Error clearing everything:', error);
    }
  };

  const {data: deptData} = useGetDepartmentsQuery(cid);
  const {data: employeeData} = useGetEmployeesQuery(cid);
  const {data, refetch} = useGetSTMProductsQuery(gdata.company);

  // Load data from local storage on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      if (isLoadingData) return; // Prevent multiple simultaneous loads

      try {
        setIsLoadingData(true);

        // Load from local storage first
        const cachedDepts = await loadDepartmentsFromStorage();
        const cachedEmployees = await loadEmployeesFromStorage();
        const cachedSelectedDept = await loadSelectedDept();
        const cachedSelectedEmployee = await loadSelectedEmployee();

        // Set form values if we have cached selections
        if (cachedSelectedDept && methods) {
          methods.setValue('it_department', cachedSelectedDept.dp_id);
        }
        if (cachedSelectedEmployee && methods) {
          methods.setValue('it_name', cachedSelectedEmployee.em_id);
        }

        // Fetch fresh data from API in background (only if not already loading)
        if (!deptData && !employeeData) {
          refetch();
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadInitialData();
  }, []);

  // Store data to local storage when API data is available
  useEffect(() => {
    if (deptData && deptData.length > 0) {
      storeDepartments(deptData);
      setLocalDepts(deptData);
    }
  }, [deptData]);

  useEffect(() => {
    if (employeeData && employeeData.length > 0) {
      storeEmployees(employeeData);
      setLocalEmployees(employeeData);
    }
  }, [employeeData]);

  // Update form values when selected department or employee changes
  useEffect(() => {
    if (selectedDept && methods) {
      try {
        methods.setValue('it_department', selectedDept.dp_id);
      } catch (error) {
        console.error('Error setting department value:', error);
      }
    }
  }, [selectedDept]);

  useEffect(() => {
    if (selectedEmployee && methods) {
      try {
        methods.setValue('it_name', selectedEmployee.em_id);
      } catch (error) {
        console.error('Error setting employee value:', error);
      }
    }
  }, [selectedEmployee]);

  const {data: groupData} = useGetSTMGroupsQuery(gdata.company);

  // Use local storage data first, then API data as fallback
  const currentDeptData =
    localDepts && localDepts.length > 0 ? localDepts : deptData || [];
  const currentEmployeeData =
    localEmployees && localEmployees.length > 0
      ? localEmployees
      : employeeData || [];

  const depts: any = currentDeptData?.map((value, key) => {
    return {
      label: value.dp_name,
      id: value.dp_id,
      values: value,
    };
  });

  const names: any = currentEmployeeData?.map((value, key) => {
    return {
      label: value.em_name,
      id: value.em_id,
      values: value,
    };
  });

  // Indent lines become department issues, which take consumables only (server enforces it too).
  // gr_type is absent on older servers -> everything counts as consumables.
  const isConsumable = (t: any) => {
    const x = String(t ?? 'consumables').trim().toLowerCase();
    return x === '' || x === 'consumables';
  };

  const groups: any = groupData?.filter((g: any) => isConsumable(g.gr_type)).map((value, _key) => {
    return {
      label: value.gr_name,
      id: value.gr_id,
      values: value,
    };
  });

  const products1: any = data?.filter((v: any) => isConsumable(v.gr_type)).map((value: any, _key: any) => {
    return {
      pr_group: value.pr_group,
      pr_id: value.pr_id,
      pr_name: value.pr_name,
      pr_srate: value.pr_srate,
      pr_qty: 0,
      pr_grname: value.pr_grname,
      pr_nameed: value.pr_name == '',
      value: value,
    };
  });

  const renderSeparator = () => {
    return (
      <View
        style={{
          height: 1,
          width: '86%',
          backgroundColor: '#CED0CE',
          marginLeft: '14%',
        }}
      />
    );
  };

  const handleCart = (item: item, qty: number) => {
    const rate =
      Number(item.pr_srate) > 0
        ? Number(item.pr_srate)
        : Number(quantities[item.pr_id]?.rate) || 0;

    // Don't add if rate is 0 and user hasn't entered one yet (only when
    // adding quantity, not removing) - otherwise an undefined/NaN rate
    // silently reaches id_rate in the submitted indent.
    if (rate <= 0 && qty > 0 && Number(item.pr_srate) === 0) {
      ShowToast('Please enter rate first', 'error');
      return;
    }

    if ((quantities[item.pr_id]?.quantity || 0) + qty >= 0) {
      const amt = ((quantities[item.pr_id]?.quantity || 0) + qty) * rate;

      setQuantities({
        ...quantities,
        [item.pr_id]: {
          descr: item.pr_name,
          quantity: (Number(quantities[item.pr_id]?.quantity) || 0) + qty,
          rate: rate,
          nameed: item.pr_nameed,
        },
      });
    } else {
      const newQuantities = {...quantities};
      delete newQuantities[item.pr_id];

      setQuantities(newQuantities);
    }
  };

  const handleCartClick = () => {
    setCart(!cart);

    setGroup(0);
    setSearch('');
  };

  const [products, setProducts] = useState(products1);

  useEffect(() => {
    const prods = products1?.filter((v: any) => {
      return Number(v.pr_group) == Number(group) || Number(group) == 0;
    });

    if (search !== '' && !isNaN(parseFloat(search)) && !cart) {
      const d = products1.filter((o: any) => o.value.pr_code == search);
      setProducts(d);
    } else if (search.length >= 3 && !cart) {
      const options = {
        includeScore: true,
        useExtendedSearch: true,
        keys: ['pr_name'],
      };

      const fuse = new Fuse(prods, options);
      const b = fuse.search(search);
      const c = b.map(v => {
        return v.item;
      });

      setProducts(c);
    } else if (search == '') {
      setProducts(
        prods?.filter((v: any) => {
          return (quantities?.hasOwnProperty(v.pr_id) && cart) || !cart;
        }),
      );
    }
  }, [search, data, cart, group]);

  let initialValues: any = {
    it_id: 0,
    it_company: cid,
    it_date:
      moment(gdata.today).format('YYYY/MM/DD') + moment().format(' HH:mm:ss'),
    it_no: '',
    it_department: selectedDept?.dp_id || '',
    it_name: selectedEmployee?.em_id || '',
    it_remarks: '',
    it_flag: '1',
    it_uid: gdata.user,
    it_cdt: moment().format('YYYY/MM/DD HH:mm:ss'),
    it_muid: gdata.user,
    it_mdt: moment().format('YYYY/MM/DD HH:mm:ss'),
    it_details: [],
  };

  const {...methods} = useForm<any>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const [insertIndent] = useInsertIndentMutation();
  const [updateIndent] = useUpdateIndentMutation();

  const onSubmit: SubmitHandler<st_eindent> = async values => {
    if (isSaving) {
      return;
    }

    try {
      let itms: st_eindentdetails[] = [];
      let i = 1;
      for (const [id, product] of Object.entries(quantities)) {
        const {descr, quantity, rate, remarks} = product;

        if (quantity > 0)
          itms.push({
            id_id: 0,
            id_srid: i++,
            id_prod: Number(id),
            id_descr: descr,
            id_qty: quantity,
            id_rate: rate,
            id_uom: 1,
          });
      }
      values.it_details = itms;
      values.it_date = new Date();

      if (values.it_details.length === 0) {
        ShowToast('Select Menu Items', 'error');

        return;
      }
      setIsSaving(true);

      if (Number(values.it_id) !== 0) {
        const dat: any = await updateIndent(values).unwrap();

        resetFormWithSelections();
        ShowToast(dat.it_no + ' Updated Sucessfully');
      } else {
        const dat: any = await insertIndent(values).unwrap();

        resetFormWithSelections();
        ShowToast(dat.it_no + ' Saved Sucessfully');
      }

      setIsSaving(false);
      navigation.goBack();
    } catch (e: any) {
      ShowToast(e?.data?.message || e?.message || 'Failed to save indent', 'error');
      setIsSaving(false);
    }
  };

  const onError: SubmitErrorHandler<st_eindent> = (errors, e) => {
    // Handle form validation errors
    Object.keys(errors).map((fieldName: string, i: number) => {
      // Handle validation errors
    });
    return false;
  };

  const [billfooter, setBillfooter] = useState({
    stotqty: '0.00',
    stotamt: '0.00',
  });

  const CalcTot = () => {
    let totQty = 0,
      totAmt = 0;
    for (const [_id, product] of Object.entries(quantities)) {
      const {quantity, rate} = product;

      if (Number(quantity) > 0) {
        totQty += Number(quantity);
        totAmt += Number(rate) * Number(quantity);
      }
    }
    setBillfooter({
      stotqty: totQty.toFixed(2),
      stotamt: totAmt.toFixed(2),
    });
  };

  useEffect(() => {
    CalcTot();
  }, [quantities]);

  const updateItemField = (field: string, text: string, itemId: string) => {
    setQuantities(prevQuantities => {
      const currentItem = prevQuantities[itemId] || {
        quantity: 0,
        remarks: '',
        descr: '',
        rate: 0,
        nameed: true,
      };

      const updatedItem = {
        ...currentItem,
        [field]: text,
      };

      return {
        ...prevQuantities,
        [itemId]: updatedItem,
      };
    });
  };

  const renderItem = (item: any) => {
    return item ? (
      <View style={styles.conLstItem} key={item.pr_id}>
        <View style={styles.conLstData}>
          {group == 0 ? (
            <Row>
              <Col>
                <Text style={styles.descr}>{item?.pr_grname}</Text>
              </Col>
            </Row>
          ) : null}
          <Row>
            <Col>
              {item?.pr_nameed && quantities[item.pr_id]?.quantity > 0 ? (
                <FormProvider {...methods}>
                  <TextInput
                    name={`descr.${item.pr_id}`}
                    label=""
                    placeholder={tp("Product Name")}
                    onChangeText={txt =>
                      updateItemField('descr', txt, item.pr_id)
                    }
                  />
                </FormProvider>
              ) : (
                <View style={{flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6}}>
                  <Text style={styles.heading}>{item?.pr_name}</Text>
                  {item?.value?.pr_stock !== undefined && item?.value?.pr_stock !== null ? (
                    <Text
                      style={{
                        fontSize: 11,
                        fontWeight: '600',
                        color: Number(item.value.pr_stock) > 0 ? '#2e7d32' : '#c62828',
                        backgroundColor: Number(item.value.pr_stock) > 0 ? '#e8f5e8' : '#ffebee',
                        paddingHorizontal: 6,
                        paddingVertical: 2,
                        borderRadius: 4,
                        overflow: 'hidden'
                      }}>
                      <T>Stock:</T> {Number(item.value.pr_stock).toFixed(2)}
                    </Text>
                  ) : null}
                </View>
              )}
            </Col>
          </Row>

          <Row style={styles.rowSec}>
            <Col>
              {Number(item?.pr_srate) == 0 ? (
                <FormProvider {...methods}>
                  <TextInput
                    name={`rate.${item.pr_id}`}
                    label=""
                    placeholder={tp("Rate")}
                    onChangeText={txt =>
                      updateItemField('rate', txt, item.pr_id)
                    }
                    keyboardType="numeric"
                  />
                </FormProvider>
              ) : (
                <Text style={styles.rate}>{'Rs. ' + item?.pr_srate}</Text>
              )}
            </Col>
            <Col style={styles.iconCol}>
              <TouchableOpacity
                onPress={() => handleCart(item, -1)}
                style={{
                  backgroundColor: '#FF6B6B',
                  padding: 1,
                  borderRadius: 50,
                  shadowColor: '#000',
                  shadowOffset: {width: 0, height: 4},
                  shadowOpacity: 0.3,
                  shadowRadius: 4,
                  elevation: 6,
                }}>
                <Icon
                  name="minus-circle"
                  size={42}
                  color="#FFF"
                  style={{alignSelf: 'center'}}
                />
              </TouchableOpacity>
            </Col>
            <Col>
              <Text style={styles.qty}>
                <FormProvider {...methods}>
                  <TextInput
                    name={`quantity.${item.pr_id}`}
                    label=""
                    placeholder={tp("Qty")}
                    value={quantities[item.pr_id]?.quantity.toString()}
                    onChangeText={txt =>
                      updateItemField('quantity', txt, item.pr_id)
                    }
                    keyboardType="numeric"
                    style={styles.qty}
                  />
                </FormProvider>
              </Text>
            </Col>
            <Col style={styles.iconCol}>
              <TouchableOpacity
                onPress={() => handleCart(item, 1)}
                style={{
                  backgroundColor: '#28A745',
                  padding: 1,
                  borderRadius: 50,
                  shadowColor: '#000',
                  shadowOffset: {width: 0, height: 4},
                  shadowOpacity: 0.3,
                  shadowRadius: 4,
                  elevation: 6,
                }}>
                <Icon
                  name="plus-circle"
                  size={42}
                  color="#FFF"
                  style={{alignSelf: 'center'}}
                />
              </TouchableOpacity>
            </Col>
            <Col>
              <Text style={styles.amt}>
                {'Rs.' +
                  (Number(item.pr_srate) > 0
                    ? Number(item.pr_srate)
                    : quantities[item.pr_id]?.rate) *
                    (!isNaN(Number(quantities[item.pr_id]?.quantity))
                      ? quantities[item.pr_id]?.quantity
                      : 0)}
              </Text>
            </Col>
          </Row>
        </View>
      </View>
    ) : (
      <View />
    );
  };

  const [showHead, setShowHead] = useState(false);

  return (
    <View style={styles.app}>
      {/* Modern Header */}
      <View style={styles.modernHeaderContainer}>
        <Text style={styles.modernHeaderTitle}><T>Indent</T></Text>
      </View>
      {(selectedDept || selectedEmployee) && (
        <View
          style={{
            backgroundColor: '#d4edda',
            padding: 8,
            borderBottomWidth: 1,
            borderBottomColor: '#c3e6cb',
          }}>
          <Text style={{color: '#155724', fontSize: 12, textAlign: 'center'}}>
            <T>Last used:</T> {selectedDept?.dp_name || 'N/A'} -{' '}
            {selectedEmployee?.em_name || 'N/A'}
          </Text>
        </View>
      )}
      <FormProvider {...methods}>
        {showHead ? (
            <>
              <Row>
                <Col>
                  <BTComboBox
                    name="it_department"
                    label="Department"
                    data={depts}
                    placeholder={tp("Department")}
                    onValChange={handleDeptChange}
                  />
                </Col>
                <Col>
                  <BTComboBox
                    name="it_name"
                    label="Name"
                    data={names}
                    placeholder={tp("Incharge Name")}
                    onValChange={handleEmployeeChange}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <TextInput
                    name="it_remarks"
                    label="Remarks"
                    placeholder={tp("Remarks")}
                  />
                </Col>
              </Row>
            </>
          ) : null}
          <Row>
            <Col>
              <View style={styles.button}>
                <BTButton
                  title="Save"
                  color="#146C94"
                  onPress={methods.handleSubmit(onSubmit, onError)}
                  onLongPress={() => {}}
                  disabled={isSaving}
                />
              </View>
            </Col>
            <Col>
              <View style={styles.button}>
                <BTButton
                  title="Clear"
                  color="#146C94"
                  onPress={resetFormWithSelections}
                  onLongPress={clearEverything}
                  disabled={isSaving}
                />
              </View>
            </Col>
            <Col>
              <View style={styles.button}>
                <BTButton
                  title="View"
                  color={cart ? '#19A7CE' : '#146C94'}
                  onPress={() => handleCartClick()}
                  onLongPress={() => {}}
                  disabled={isSaving}
                />
              </View>
            </Col>
            <Col>
              <View style={styles.button}>
                <BTButton
                  title={`${
                    showHead
                      ? 'hide'
                      : depts
                          ?.find(
                            (v: any) =>
                              v.id == methods.getValues('it_department'),
                          )
                          ?.label.slice(0, 4) || 'Nill'
                  }`}
                  onLongPress={() => {}}
                  color={showHead ? '#19A7CE' : '#146C94'}
                  onPress={() => setShowHead(prev => !prev)}
                  disabled={isSaving}
                />
              </View>
            </Col>
          </Row>
          {cart ? (
            <View style={{padding: 10}}>
              <Row>
                <Col>
                  <Text style={styles.heading}><T>Total Amount</T></Text>
                </Col>
                <Col>
                  <Text>{billfooter.stotqty}</Text>
                </Col>
                <Col>
                  <Text style={[styles.amt, styles.heading]}>
                    {billfooter.stotamt}
                  </Text>
                </Col>
              </Row>
            </View>
          ) : null}
          {cart ? null : (
            <Row>
              <Col>
                <BTComboBox
                  name="group"
                  label="Group"
                  data={groups}
                  placeholder={tp("Select Group")}
                  onValChange={(v: any) => setGroup(v)}
                />
              </Col>
            </Row>
          )}
        </FormProvider>
      {cart ? null : (
        <Row>
          <Col>
            <TextInput1
              style={styles.text}
              onChangeText={setSearch}
              value={search}
              placeholder={tp("Search")}
            />
          </Col>
        </Row>
      )}
      <View style={{flex: 1}}>
        <FlatList
          ItemSeparatorComponent={renderSeparator}
          data={products}
          renderItem={({item}) => renderItem(item)}
          onEndReachedThreshold={0.1}
          contentContainerStyle={{paddingBottom: 80}}
          keyboardShouldPersistTaps="handled"
        />
      </View>
    </View>
  );
};

export default POSProduct;

